'use client';

import { useMemo, useRef } from 'react';
import { useShallow } from 'zustand/shallow';
import { Menu, BodyDefault, LabelDefault } from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import { Add01Icon } from '@terra/ui-icons';
import { Portal } from '@ark-ui/react';
import { useTranslations } from 'next-intl';
import {
  useProjectStore,
  useSelectedCompany,
  type PortalTarget,
} from '../../../store/projectStore';
import { useTargets } from '../../../hooks/useTargets';
import { useAutoSelectCompany } from '../../../hooks/useAutoSelectCompany';
import { useUserTenants } from '../../../hooks/api/useUserTenants';
import { useOpenWorkspace } from '../../../hooks/useOpenWorkspace';
import { useCurrentWorkspace } from '../../../store/workspaceStore';
import { getTargetTypeDisplay } from '../../../utils/target-scope.utils';
import { getAttackSurfaceIcon } from './attackSurfaces';
import { AttackSurfaceSubmenu } from './AttackSurfaceSubmenu';
import { useAttackSurfaceGroups } from './useAttackSurfaceGroups';
import { TenantSwitchSubmenu } from './TenantSwitchSubmenu';
import { MenuDivider } from '../MenuDivider';
import { TargetSelectorMenuItemRow } from './TargetSelectorMenuItemRow';
import { TargetSelectorTrigger } from './TargetSelectorTrigger';
import { formatTargetSelectorLabel } from './formatTargetSelectorLabel';

const MENU_GAP_PX = 4;

type TargetSelectorProps = {
  collapsed: boolean;
};

export function TargetSelector({ collapsed }: TargetSelectorProps) {
  const triggerContainerRef = useRef<HTMLDivElement>(null);
  const tSelector = useTranslations('proxy.applicationSelector');
  const tCommon = useTranslations('common');
  const tWorkspaces = useTranslations('proxy.workspaces');
  const workspace = useCurrentWorkspace();
  const selectedCompany = useSelectedCompany();
  const { companyId, targetId, targetType, selectedTarget, setTarget } =
    useProjectStore(
      useShallow((state) => ({
        companyId: state.companyId,
        targetId: state.targetId,
        targetType: state.targetType,
        selectedTarget: state.selectedTarget,
        setTarget: state.setTarget,
      })),
    );
  const { targets, catalogSliceStatus, isLoading, isFetched, error } =
    useTargets({
      companyId,
    });
  const { isCompanyReady, isResolvingCompany } = useAutoSelectCompany();
  const { tenants, currentTenantId } = useUserTenants();

  const tenantName = useMemo(() => {
    const authTenantName = tenants.find(
      (tenant) => tenant.id === currentTenantId,
    )?.name;

    return authTenantName ?? selectedCompany?.name ?? '';
  }, [tenants, currentTenantId, selectedCompany?.name]);
  const { openWorkspace, focusTargetElsewhere, openPicker } = useOpenWorkspace({
    tenantId: currentTenantId ?? null,
    tenantName,
  });
  const openInNewWindow = workspace
    ? (target: PortalTarget) => void openWorkspace(target)
    : undefined;
  const handleSelectTarget = async (target: PortalTarget): Promise<void> => {
    const focusedElsewhere = workspace
      ? await focusTargetElsewhere(target)
      : false;

    if (!focusedElsewhere) {
      setTarget(target);
    }
  };

  const currentTargetName =
    selectedTarget?.name ?? tSelector('selectApplicationAriaLabel');
  const selectTargetAriaLabel = tSelector('selectApplicationAriaLabel');
  const showSwitchTenant = tenants.length > 1;
  const tenantContextName = showSwitchTenant
    ? tenantName || undefined
    : undefined;
  const SelectedSurfaceIcon = getAttackSurfaceIcon(targetType);
  const isWorkspacePreparing = isResolvingCompany;
  const isWorkspaceNotReady = !isResolvingCompany && !isCompanyReady;
  const isCatalogLoading =
    isLoading || (isCompanyReady && Boolean(companyId) && !isFetched && !error);

  const enabledSurfaces = useAttackSurfaceGroups({
    targets,
    catalogSliceStatus,
  });

  return (
    <Box ref={triggerContainerRef} w="full" position="relative">
      <Menu.Root
        lazyMount
        unmountOnExit
        positioning={{
          strategy: 'fixed',
          placement: 'bottom-start',
          gutter: MENU_GAP_PX,
          flip: false,
          slide: false,
          overlap: false,
          overflowPadding: 0,
          getAnchorRect: () => {
            const trigger = triggerContainerRef.current;

            if (!trigger) {
              return null;
            }

            const rect = trigger.getBoundingClientRect();

            return {
              x: rect.x,
              y: rect.y,
              width: rect.width,
              height: rect.height,
            };
          },
          updatePosition: async ({ updatePosition, floatingElement }) => {
            await updatePosition();
            const trigger = triggerContainerRef.current;

            if (!trigger || !floatingElement) {
              return;
            }

            const rect = trigger.getBoundingClientRect();

            floatingElement.style.setProperty(
              '--x',
              `${Math.round(rect.left)}px`,
            );
            floatingElement.style.setProperty(
              '--y',
              `${Math.round(rect.bottom + MENU_GAP_PX)}px`,
            );
          },
        }}
      >
        <TargetSelectorTrigger
          collapsed={collapsed}
          ariaLabel={selectTargetAriaLabel}
          tooltipTitle={formatTargetSelectorLabel({
            primaryLabel: currentTargetName,
            secondaryLabel: tenantContextName,
          })}
          icon={SelectedSurfaceIcon}
          primaryLabel={currentTargetName}
          secondaryLabel={tenantContextName}
        />
        <Portal>
          <Menu.Positioner zIndex="popover">
            <Menu.Content
              minW="320px"
              p="2"
              gap="1"
              borderRadius="xl"
              borderWidth="1px"
              borderColor="border.neutral.subtle"
              boxShadow="md"
              bg="bg.neutral.default"
              maxH="calc(100vh - var(--y, 0px) - 16px)"
              overflowY="auto"
              zIndex="popover"
            >
              {tenantContextName && (
                <Box px="2" py="1">
                  <LabelDefault
                    as="span"
                    color="fg.neutral.muted"
                    data-bi-unmask="true"
                  >
                    {tenantContextName}
                  </LabelDefault>
                </Box>
              )}
              {selectedTarget && (
                <>
                  {tenantContextName && <MenuDivider />}
                  <Box p="2">
                    <TargetSelectorMenuItemRow
                      icon={<SelectedSurfaceIcon size={14} />}
                      label={selectedTarget.name}
                      description={getTargetTypeDisplay(targetType).label}
                      showCheck
                    />
                  </Box>
                </>
              )}
              <MenuDivider />
              {isWorkspacePreparing ? (
                <Box px="3" py="3" textAlign="center">
                  <BodyDefault color="fg.neutral.muted">
                    {tSelector('preparingWorkspace')}
                  </BodyDefault>
                </Box>
              ) : isWorkspaceNotReady ? (
                <Box px="3" py="3" textAlign="center">
                  <BodyDefault color="fg.neutral.muted">
                    {tSelector('workspaceNotReady')}
                  </BodyDefault>
                </Box>
              ) : isCatalogLoading ? (
                <Box px="3" py="3" textAlign="center">
                  <BodyDefault color="fg.neutral.muted">
                    {tCommon('loading')}
                  </BodyDefault>
                </Box>
              ) : error ? (
                <Box px="3" py="3" textAlign="center">
                  <BodyDefault color="fg.neutral.muted">
                    {tSelector('applicationsLoadError')}
                  </BodyDefault>
                </Box>
              ) : (
                enabledSurfaces.map(
                  ({ surface, label, targets: surfaceTargets }) => (
                    <AttackSurfaceSubmenu
                      key={surface.targetType}
                      surface={surface}
                      label={label}
                      targets={surfaceTargets}
                      currentTargetId={targetId}
                      currentTargetType={targetType}
                      onSelectTarget={(target) =>
                        void handleSelectTarget(target)
                      }
                      onOpenInNewWindow={openInNewWindow}
                    />
                  ),
                )
              )}
              {showSwitchTenant && (
                <>
                  <MenuDivider />
                  <TenantSwitchSubmenu />
                </>
              )}
              {workspace && (
                <>
                  <MenuDivider />
                  <Menu.Item
                    value="open-another-workspace"
                    onClick={() => void openPicker()}
                    p="2"
                  >
                    <TargetSelectorMenuItemRow
                      icon={<Add01Icon size={14} />}
                      label={tWorkspaces('openAnother')}
                    />
                  </Menu.Item>
                </>
              )}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    </Box>
  );
}
